import React, {useState, useEffect, useRef} from 'react';
import ProductLoader from './ProductLoader/ProductLoader';
import ProductTag from './ProductTag/ProductTag';
import ProductRating from './ProductRating/ProductRating';
import ImageGallery from '../ImageGallery/ImageGallery';
import ImageWrapper from '../ImageWrapper/ImageWrapper';
import ModelViewer from '../ModelViewer/ModelViewer';
import {Tag} from '../../models/Product';
import Color from '../../helpers/Color';
import Currency from '../../config/Currency';
import CurrencyFormat from 'react-currency-format';
import Text from '../../helpers/Text';
import Viewport from '../../helpers/Viewport';
import HTMLElement from '../../helpers/HTMLElement';
import {PRODUCT_BOX_3D_DEFAULT_CONFIG} from '../../config/App';
import defaultDeliveryIcon from './images/delivery.svg';
import defaultFreeDeliveryIcon from './images/free-delivery.svg';
import defaultPickupIcon from './images/pickup.svg';
import default3dIcon from './images/3d.gif';
import defaultShopIcon from './images/shop.png';
import defaultTruckIcon from './images/truck.png';
import defaultPlaneIcon from './images/plane.png';
import './ProductBox.css';

function ProductBox({
    viewRef,
    idPrefix,
    index,
    category,
    product,
    showSku = true,
    showIcons = true,
    active = false,
    clientName,
    design = 'full',
    _3dIcon,
    isFeatured = false,
    shouldDrawGallery = false,
    shouldDraw3D = false,
    taxMultiplier,
    taxLabel,
    discountBackgroundColor,
    discountLabelColor,
    exclusivePriceLabelColor,
    exclusivePriceIcon,
    offerBackgroundColor,
    offerLabelColor,
    offerLabel,
    offerIcon,
    deliveryIcon,
    deliveryLabelColor,
    deliveryBackgroundColor,
    freeDeliveryIcon,
    freeDeliveryLabelColor,
    freeDeliveryBackgroundColor,
    pickupIcon,
    pickupLabelColor,
    pickupBackgroundColor,
    infoTagBorderColor,
    infoTagBackgroundColor,
    infoTagLabelColor,
    otherTagsBackgroundColor,
    otherTagsLabelColor,
    sellerLabelColor,
    mainSellerIcon,
    taxDisclaimerBackgroundColor,
    taxDisclaimerLabelColor,
    featuredDefaultIcon,
    featuredDefaultLabel,
    featuredDefaultMediaWidthPercentage = 50,
    featuredWidthMultiplier = 3,
    featuredBackgroundColor,
    featuredLabelColor,
    showPulseEffect,
    pulseEffectColor,
    pulseEffectIterations,
    selected,
    selectedBorderColor,
    showTopBanner = true,
    showSeller,
    showDelivery,
    showRating,
    showTags,
    showInfoTagOnImage,
    infoTagLabel,
    infoTagProperty,
    visible,
    disabled,
    onClick
}) {
    const pulseRef = useRef(null);
    const wrapperRef = useRef(null);
    const tagsContainerRef = useRef(null);
    const tagsRef = useRef(null);
    const modelViewerRef = useRef(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isHovering, setIsHovering] = useState(false);
    const [isLoadingImage, setIsLoadingImage] = useState(true);
    const [isLoading3DModel, setIsLoading3DModel] = useState(false);
    const [pulseEffect, setPulseEffect] = useState(undefined);
    const [tagsAnimation, setTagsAnimation] = useState(undefined);
    const currencyFormat = Currency.CLP;
    const tagGroup = `product-box-tag-${product.id}`;
    const boxWidth = 280;
    const boxMargin = 10;
    const maxGalleryImages = 5;

    useEffect(() => {
        setIsLoading(false);
        shouldStartTagsScroll();
        return () => {
            stopPulseEffect();
            stopTagsScroll();
        }
    }, []);

    useEffect(() => {
        if (show3DModel()) setIsLoading3DModel(true);
    }, [shouldDraw3D]);

    useEffect(() => {
        if (showPulseEffect) startPulseEffect();
        else stopPulseEffect();
    }, [pulseEffectColor, showPulseEffect]);

    useEffect(() => {
        shouldStartTagsScroll();
    }, [product.delivery, product.freeDelivery, product.pickup, product.fastPickup, product.fastDelivery, product.tags, product.paymentOptions]);

    const onImageLoad = () => {
        setIsLoadingImage(false);
    };

    const on3DModelLoad = () => {
        setTimeout(() => {
            setIsLoading3DModel(false);
        }, 1000);
    };

    const handleMouseEnter = () => {
        setIsHovering(true);
    };

    const handleMouseLeave = () => {
        setIsHovering(false);
    };

    const handleClick = () => {
        if (!disabled && onClick) onClick(category, product, index);
    };

    const showBanner = () => {
        return _isFeatured() || isFeaturedButDoesNotFit() || canShowOfferBanner();
    };

    const canShowFeaturedBanner = () => {
        return !!product.featuredData && !!product.featuredData.label;
    };

    const canShowOfferBanner = () => {
        return Boolean(product.exclusivePrice) && !active && showTopBanner;
    };

    const getPriceWithTaxes = (price) => {
        return taxMultiplier ? price * taxMultiplier : price;
    };

    const hasSeller = () => {
        return showSeller && ((product.externalSale && !!product.sellerName) || (!product.externalSale && !!clientName));
    };

    const hasDelivery = () => {
        return showDelivery && product.clientDelivery && !!clientName;
    };

    const hasInternationalShipping = () => {
        return showDelivery && !product.clientDelivery && product.internationalShipping;
    };

    const _isFeatured = () => {
        return design !== 'simple' && isFeatured && fitsAsFeatured();
    };

    const isFeaturedButDoesNotFit = () => {
        return design !== 'simple' && isFeatured && !fitsAsFeatured();
    };

    const fitsAsFeatured = () => {
        const viewport = Viewport.dimensions;
        const scrollbarWidth = viewRef && viewRef.current ? HTMLElement.getScrollbarWidth(viewRef.current) : 0;
        return getFeaturedBoxWidth() + (boxMargin * 2) + scrollbarWidth <= viewport.width;
    };

    const startPulseEffect = () => {
        if (pulseRef && pulseRef.current) {
            stopPulseEffect();
            const rgb = Color.hexToRgb(pulseEffectColor) || Color.hexToRgb(pulseEffectColor) || {r: 0, g: 91, b: 128};
            const pulseEffect = pulseRef.current.animate([
                {boxShadow: `0 0 0 0 rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.9)`},
                {boxShadow: `0 0 0 10px rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0)`},
                {boxShadow: `0 0 0 0 rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0)`}
            ], {
                duration: 1500,
                iterations: pulseEffectIterations || Infinity
            });
            setPulseEffect(pulseEffect);
        }
    };

    const stopPulseEffect = () => {
        if (pulseEffect) {
            pulseEffect.cancel();
            setPulseEffect(undefined);
        }
    };

    const shouldStartTagsScroll = () => {
        const tagsContainerWidth = tagsContainerRef && tagsContainerRef.current ? tagsContainerRef.current.offsetWidth : 0;
        const tagsWrapperWidth = tagsContainerRef && tagsContainerRef.current ? tagsContainerRef.current.scrollWidth : 0;
        const shouldStart = tagsWrapperWidth > (tagsContainerWidth + 1); // +1 to avoid rounding issues
        if (shouldStart) {
            setTimeout(() => {
                startTagsScroll();
            }, 2000);
        } else stopTagsScroll();
    };

    const startTagsScroll = () => {
        if (tagsRef && tagsRef.current && !tagsAnimation) {
            stopTagsScroll();
            const tagsContainerWidth = tagsContainerRef && tagsContainerRef.current ? tagsContainerRef.current.offsetWidth : 0;
            const tagsWrapperWidth = tagsContainerRef && tagsContainerRef.current ? tagsContainerRef.current.scrollWidth : 0;
            const scrollPercent = 100 - Math.round((tagsContainerWidth * 100) / tagsWrapperWidth);
            const _tagsAnimation = tagsRef.current.animate([
                {transform: 'translateX(0%)'},
                {transform: `translateX(${scrollPercent * -1}%)`},
                {transform: 'translateX(0%)'}
            ], {
                duration: 15 * 1000,
                iterations: Infinity
            });
            setTagsAnimation(_tagsAnimation);
        }
    };

    const stopTagsScroll = () => {
        if (tagsAnimation) {
            tagsAnimation.cancel();
            setTagsAnimation(undefined);
        }
    };

    const buildTags = () => {
        let baseTags = [];
        if (!isTagShownAsInfoTag('delivery') && product.delivery) baseTags.push(new Tag({key: 'delivery', value: 'Despacho', type: 'label', icon: deliveryIcon || defaultDeliveryIcon, labelColor: deliveryLabelColor, backgroundColor: deliveryBackgroundColor, borderColor: deliveryBackgroundColor}));
        if (!isTagShownAsInfoTag('freeDelivery') && product.freeDelivery) baseTags.push(new Tag({key: 'freeDelivery', value: 'Despacho Gratis', type: 'label', icon: freeDeliveryIcon || defaultFreeDeliveryIcon, labelColor: freeDeliveryLabelColor, backgroundColor: freeDeliveryBackgroundColor, borderColor: freeDeliveryBackgroundColor}));
        if (!isTagShownAsInfoTag('pickup') && product.pickup) baseTags.push(new Tag({key: 'pickup', value: 'Retiro', type: 'label', icon: pickupIcon || defaultPickupIcon, labelColor: pickupLabelColor, backgroundColor: pickupBackgroundColor, borderColor: pickupBackgroundColor}));
        if (!isTagShownAsInfoTag('fastPickup') && product.fastPickup) baseTags.push(new Tag({key: 'fastPickup', value: 'Retira mañana', type: 'label', icon: undefined, labelColor: otherTagsLabelColor, backgroundColor: otherTagsBackgroundColor, borderColor: otherTagsBackgroundColor}));
        if (!isTagShownAsInfoTag('fastDelivery') && product.fastDelivery) baseTags.push(new Tag({key: 'fastDelivery', value: 'Llega mañana', type: 'label', icon: undefined, labelColor: otherTagsLabelColor, backgroundColor: otherTagsBackgroundColor, borderColor: otherTagsBackgroundColor}));
        product.tags.forEach((tag) => {
            if (!showInfoTagOnImage || (showInfoTagOnImage && infoTagProperty !== tag.key)) {
                const newTag = tag.clone();
                newTag.icon = undefined;
                newTag.labelColor = otherTagsLabelColor;
                newTag.backgroundColor = otherTagsBackgroundColor;
                newTag.borderColor = otherTagsBackgroundColor;
                newTag.value = `${newTag.key}: ${newTag.value}`;
                baseTags.push(newTag);
            }
        });
        let paymentOptionsTags = product.paymentOptions.map((option) => (
            new Tag({key: option.id, value: option.label, type: 'label', icon: option.icon, labelColor: otherTagsLabelColor, backgroundColor: otherTagsBackgroundColor, borderColor: otherTagsBackgroundColor})
        ));
        return baseTags.concat(paymentOptionsTags);
    };

    const shouldShowInfoTagOnImage = () => {
        return showInfoTagOnImage && !!infoTagLabel && !!infoTagProperty && !!product[infoTagProperty];
    };

    const isTagShownAsInfoTag = (key) => {
        return shouldShowInfoTagOnImage() && infoTagProperty === key;
    };

    const hasEnoughImagesForGallery = () => {
        return product.multimedia && product.multimedia.gallery && product.multimedia.gallery.length > 1;
    };

    const hasEnough3DModelData = () => {
        return product.has3dModel && !!product.viewItSceneUrl;
    };

    const markedToBeShownAsGallery = () => {
        return !!product.featuredData && product.featuredData.mediaType === 'gallery';
    };

    const markedToBeShownAs3DModel = () => {
        return !!product.featuredData && product.featuredData.mediaType === '3d';
    };

    const canShowGallery = () => {
        return (_isFeatured() || isFeaturedButDoesNotFit()) && markedToBeShownAsGallery() && hasEnoughImagesForGallery();
    };

    const canShow3DModel = () => {
        return (_isFeatured() || isFeaturedButDoesNotFit()) && markedToBeShownAs3DModel() && hasEnough3DModelData();
    };

    const showGallery = () => {
        return canShowGallery() && shouldDrawGallery;
    };

    const show3DModel = () => {
        return canShow3DModel() && shouldDraw3D;
    };

    const build3DModelUrl = () => {
        if (!product.viewItSceneUrl) return undefined;
        const url = new URL(product.viewItSceneUrl);
        url.searchParams.set('showProductLink', 'false');
        url.searchParams.set('showDimensions', 'false');
        url.searchParams.set('showAr', 'false');
        url.searchParams.set('showTryOnLink', 'false');
        url.searchParams.set('showShare', 'false');
        url.searchParams.set('drawDimensions', 'false');
        url.searchParams.set('showInteractionTooltip', 'false');
        url.searchParams.set('whiteLabel', 'true');
        url.searchParams.set('forceCameraFov', encodeURIComponent(PRODUCT_BOX_3D_DEFAULT_CONFIG.fov));
        url.searchParams.set('forceCameraDistance', encodeURIComponent(PRODUCT_BOX_3D_DEFAULT_CONFIG.radius));
        return url.toString();
    };

    const getMediaWidthPercentage = () => {
        return product.featuredData && product.featuredData.mediaWidthPercentage ? product.featuredData.mediaWidthPercentage : featuredDefaultMediaWidthPercentage || 50;
    };

    const getNormalBoxHeight = () => {
        let height = 679;
        if (!showTopBanner) height -= 60;
        if (!showSku) height -= 15 + 15; // 15px from sku element height, and another 15 for the next element top margin that is not needed when the sku is not shown.
        if (!showSeller) height -= 48;
        if (!showDelivery) height -= 48;
        if (!showRating) height -= 32;
        if (!showTags) height -= 47;
        return height;
    };

    const getFeaturedBoxWidth = () => {
        return (boxWidth * featuredWidthMultiplier) + (boxMargin * (featuredWidthMultiplier - 1) * 2);
    };

    const productBoxStyle = () => {
        let width;
        let height;
        switch (design) {
            case 'simple':
                width = boxWidth;
                height = 455;
                break;
            default: // Do not change the height values for featured products. If a the content fits in a normal box, then it will always fit in the featured box.
                width = _isFeatured() ? getFeaturedBoxWidth() : boxWidth;
                height = getNormalBoxHeight();
                break;
        }
        return {
            width: width,
            transform: `scale(${(isLoading || !visible) ? 0 : (isHovering ? 1.03 : 1)})`,
            height: height,
            margin: boxMargin
        };
    };

    const pulseCoverStyle = () => {
        return {
            border: selected ? `5px solid ${selectedBorderColor || '#005B80'}` : 'none',
        };
    };

    const activeCoverStyle = () => {
        return {
            opacity: active ? 1 : 0
        };
    };

    const productBoxBannerStyle = (backgroundColor, labelColor) => {
        return {
            background: backgroundColor || '#005B80',
            color: labelColor || '#FFFFFF'
        };
    };

    const productBoxWrapperStyle = () => {
        return {
            display: _isFeatured() ? 'flex' : 'block',
            justifyContent: _isFeatured() ? 'space-between' : 'unset',
            alignContent: _isFeatured() ? 'center' : 'unset',
            alignItems: _isFeatured() ? 'center' : 'unset',
            position: _isFeatured() ? 'relative' : 'unset',
            top: _isFeatured() ? '50%' : 'unset',
            left: _isFeatured() ? '50%' : 'unset',
            transform: _isFeatured() ? 'translate(-50%, -50%)' : 'unset',
            padding: design === 'simple' ? 0 : `${!_isFeatured() && showTopBanner ? '75px' : '15px'} 15px 15px 15px`,
            opacity: isLoadingImage ? 0 : 1
        };
    };

    const productBoxMediaContainerStyle = () => {
        const mediaWidth = getMediaWidthPercentage();
        return {
            width: _isFeatured() ? `${mediaWidth}%` : '100%',
            minWidth: _isFeatured() ? `${mediaWidth}%` : '100%',
            height: _isFeatured() ? '-webkit-fill-available' : 'auto',
        };
    };

    const productBoxImageContainerStyle = () => {
        if (_isFeatured()) {
            return {
                position: 'absolute',
                top: '50%',
                transform: 'translateY(-50%)',
                opacity: (show3DModel() && isLoading3DModel) || !show3DModel() ? 1 : 0
            };
        } else if (isFeaturedButDoesNotFit()) {
            if (show3DModel()) {
                return {
                    position: 'absolute',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    opacity: isLoading3DModel ? 1 : 0
                };
            } else {
                return {
                    position: 'relative',
                    top: 'unset',
                    transform: 'unset',
                    opacity: 1
                };
            }
        } else return {};
    };

    const productBoxGalleryContainerStyle = () => {
        if (_isFeatured()) {
            return {
                height: 'auto'
            };
        } else if (isFeaturedButDoesNotFit()) {
            return {
                height: 204
            };
        }
        else return {};
    };

    const productBox3DContainerStyle = () => {
        return {
            height: _isFeatured() && show3DModel() ? '100%' : 204,
            opacity: isLoading3DModel ? 0 : 1
        };
    };

    const productBoxInfoStyle = () => {
        const mediaWidth = getMediaWidthPercentage();
        return {
            width: _isFeatured() ? `${100 - mediaWidth}%` : '100%',
            minWidth: _isFeatured() ? `${100 - mediaWidth}%` : '100%',
            padding: _isFeatured() ? '15px' : '0',
            marginTop: _isFeatured() ? 0 : 10
        };
    };

    const productBoxBrandStyle = () => {
        return {
            marginTop: `${showSku ? `${_isFeatured() ? '20px' : '15px'}` : '0'}`
        };
    };

    const productBoxLoaderWrapperStyle = () => {
        return {
            width: selected ? 'calc(100% - 10px)' : '100%',
            opacity: isLoadingImage ? 1 : 0
        };
    };

    const discountStyle = () => {
        return {
            background: discountBackgroundColor || '#005B80',
            color: discountLabelColor || '#FFFFFF'
        };
    };

    const exclusivePriceStyle = () => {
        return {
            color: exclusivePriceLabelColor || '#005B80'
        };
    };

    const sellerNameStyle = () => {
        return {
            color: sellerLabelColor || '#111111'
        };
    };

    const taxDisclaimerStyle = () => {
        return {
            background: taxDisclaimerBackgroundColor || '#005B80',
            color: taxDisclaimerLabelColor || '#FFFFFF'
        };
    };

    const renderBanner = () => {
        let icon;
        let label;
        let backgroundColor;
        let labelColor;
        const canShowCustomFeaturedBanner =  (_isFeatured() || isFeaturedButDoesNotFit()) && canShowFeaturedBanner();
        const cantShowCustomFeaturedBanner = (_isFeatured() || isFeaturedButDoesNotFit()) && !canShowFeaturedBanner();
        const canShowCustomOfferBanner = canShowOfferBanner();
        if (canShowCustomFeaturedBanner) {
            icon = product.featuredData ? product.featuredData.icon : undefined;
            label = product.featuredData ? product.featuredData.label : undefined;
            backgroundColor = featuredBackgroundColor;
            labelColor = featuredLabelColor;
        } else if (canShowCustomOfferBanner) {
            icon = offerIcon;
            label = offerLabel || 'Oferta';
            backgroundColor = offerBackgroundColor;
            labelColor = offerLabelColor;  
        } else if (cantShowCustomFeaturedBanner) {
            icon = featuredDefaultIcon;
            label = featuredDefaultLabel || 'Recomendado para ti';
            backgroundColor = featuredBackgroundColor;
            labelColor = featuredLabelColor;
        } else return undefined;
        return (
            <div className='product-box-top-bar offer' style={productBoxBannerStyle(backgroundColor, labelColor)}>
                {icon && (
                    <div className='product-box-offer-icon'>
                        <ImageWrapper image={icon} alt='Banner' display='block' width={40} height='auto' loadDelay={0.2}/>
                    </div>
                )}
                {label}
            </div>
        );
    };

    const renderAsExclusivePrice = (price, discount) => {
        return (
            <div className={`product-box-info-price exclusive${_isFeatured() ? ' big' : ''}`} style={exclusivePriceStyle()}>
                <CurrencyFormat
                    prefix={currencyFormat.prefix}
                    suffix={currencyFormat.suffix}
                    thousandSeparator={currencyFormat.thousandsSeparator}
                    decimalSeparator={currencyFormat.decimalSeparator}
                    decimalScale={currencyFormat.decimalScale}
                    value={getPriceWithTaxes(price)}
                    displayType='text'
                />
                {exclusivePriceIcon && (
                    <div className='product-box-info-card'>
                        <ImageWrapper image={exclusivePriceIcon} alt='Card' display='block' width={35} height='auto' loadDelay={0.2}/>
                    </div>
                )}
                {discount && discount > 0 ? <div className='product-box-info-discount' style={discountStyle()}>{discount}%</div> : null}
            </div>
        );
    };

    const renderAsOfferPrice = (price, discount) => {
        return (
            <div className={`product-box-info-price offer${_isFeatured() ? ' big' : ''}`}>
                <CurrencyFormat
                    prefix={currencyFormat.prefix}
                    suffix={currencyFormat.suffix}
                    thousandSeparator={currencyFormat.thousandsSeparator}
                    decimalSeparator={currencyFormat.decimalSeparator}
                    decimalScale={currencyFormat.decimalScale}
                    value={getPriceWithTaxes(price)}
                    displayType='text'
                />
                {discount && discount > 0 ? <div className='product-box-info-discount' style={discountStyle()}>{discount}%</div> : null}
            </div>
        );
    };

    const renderAsNormalPrice = (price) => {
        return (
            <div className={`product-box-info-price normal${_isFeatured() ? ' big' : ''}`}>
                <span>Antes: </span>
                <div className='product-box-info-normal-price-wrapper'>
                    <CurrencyFormat
                        prefix={currencyFormat.prefix}
                        suffix={currencyFormat.suffix}
                        thousandSeparator={currencyFormat.thousandsSeparator}
                        decimalSeparator={currencyFormat.decimalSeparator}
                        decimalScale={currencyFormat.decimalScale}
                        value={getPriceWithTaxes(price)}
                        displayType='text'
                    />
                </div>
            </div>
        );
    };

    const renderClientIcon = () => {
        return (
            <div className='product-box-info-main-seller-icon-container'>
                <ImageWrapper image={mainSellerIcon} alt='Main' display='block' width={20} height='auto' loadDelay={0.2}/>
            </div>
        );
    };

    const render3dIcon = (onImage) => {
        return (
            <div className={`product-box-3d${onImage && _isFeatured() ? ' right' : ''}`}>
                <ImageWrapper image={_3dIcon ? _3dIcon : default3dIcon} alt='Incluye modelo 3D' display='block' width={50} height='auto' loadDelay={0.2}/>
            </div>
        );
    };

    const renderTagsList = () => {
        return (
            <div ref={tagsContainerRef} className='product-box-info-tags'>
                <div ref={tagsRef} className='product-box-info-tags-wrapper'>
                    {renderTags()}
                </div>
            </div>
        );
    };

    const renderTags = () => {
        const tags = buildTags();
        return tags.map((tag, index) => {
            const isLast = index === tags.length - 1;
            const margin = isLast ? '0' : '0 5px 0 0';
            return (
                <ProductTag
                    key={`product-box-tag-${product.id}-${index}`}
                    group={tagGroup}
                    icon={tag.icon}
                    label={tag.value}
                    labelColor={tag.labelColor}
                    backgroundColor={tag.backgroundColor}
                    borderColor={tag.borderColor}
                    minWidth={80}
                    margin={margin}
                />
            );
        });
    };

    const renderImage = () => {
        let width;
        let height;
        if (design === 'simple') {
            width = '100%';
            height = 'auto';
        } else {
            width = _isFeatured() ? '100%' : 'auto';
            height = _isFeatured() ? 'auto' : 204;
        }
        return (
            <div className='product-box-image-container' style={productBoxImageContainerStyle()}>
                <ImageWrapper
                    image={product.getMainImage('m')}
                    alt={name}
                    display='block'
                    width={width}
                    height={height}
                    transition={true}
                    loadDelay={0.2}
                    onLoad={onImageLoad}
                />
            </div>
        );
    };

    const renderGallery = () => {
        let images = [];
        if (product.multimedia && product.multimedia.gallery && product.multimedia.gallery.length > 0) {
            images = product.multimedia.gallery.slice(0, maxGalleryImages).map((item) => item.m);
        } else {
            images = [product.getMainImage('m')];
        }
        return (
            <div className='product-box-gallery-container' style={productBoxGalleryContainerStyle()}>
                <ImageGallery
                    id={product.id}
                    images={images}
                    imageWidth={_isFeatured() ? '100%' : 'auto'}
                    imageHeight={_isFeatured() ? 'auto' : 204}
                    onLoad={onImageLoad}
                />
            </div>
        );
    };

    const render3DModel = () => {
        return (
            <div className='product-box-3d-container' style={productBox3DContainerStyle()}>
                <ModelViewer
                    ref={modelViewerRef}
                    url={build3DModelUrl()}
                    errorActionButtonLabelColor='#FFFFFF'
                    errorActionButtonBackgroundColor='#CD5C5C'
                    errorActionButtonBorderColor='#CD5C5C'
                    onLoad={on3DModelLoad}
                />
            </div>
        );
    };

    const renderInfoTagOnImage = () => {
        let labelColor = infoTagLabelColor || otherTagsLabelColor || undefined;
        let backgroundColor = infoTagBackgroundColor || otherTagsBackgroundColor || undefined;
        let borderColor = infoTagBorderColor || otherTagsBackgroundColor || undefined;
        return (
            <div className={`product-box-image-info-tag ${_isFeatured() ? 'top' : 'bottom'}`}>
                <ProductTag icon={undefined} label={infoTagLabel} labelColor={labelColor} backgroundColor={backgroundColor} borderColor={borderColor} margin={0}/>
            </div>
        );
    };

    const name = product.name || 'Sin nombre';

    const renderFullBox = () => {
        return (
            <div
                id={`${idPrefix ? `${idPrefix}-` : ''}${product.id}`}
                data-id={product.id}
                className={`product-box${_isFeatured() || isFeaturedButDoesNotFit() ? ' featured' : ''}${canShow3DModel() ? ' _3d' : ''}`}
                style={productBoxStyle()}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                onClick={handleClick}
            >
                <div ref={pulseRef} className='product-box-pulse-cover' style={pulseCoverStyle()}>
                    <div className='product-box-active-cover' style={activeCoverStyle()}>
                        <div className='product-box-top-bar active'>Último visto</div>
                    </div>
                    {showBanner() && renderBanner()}
                    {((showBanner() && !offerLabel && !offerIcon) || (!showBanner())) && !_isFeatured() && showIcons && product.has3dModel && render3dIcon(false)}
                    <div ref={wrapperRef} className='product-box-wrapper' style={productBoxWrapperStyle()}>
                        <div className='product-box-media-container' style={productBoxMediaContainerStyle()}>
                            {show3DModel() && render3DModel()}
                            {!show3DModel() && showGallery() ? renderGallery() : renderImage()}
                            {shouldShowInfoTagOnImage() && renderInfoTagOnImage()}
                            {((showBanner() && (offerLabel || offerIcon)) || _isFeatured()) && showIcons && product.has3dModel && render3dIcon(true)}
                        </div>
                        <div className='product-box-info' style={productBoxInfoStyle()}>
                            {_isFeatured() && <div className='product-box-featured-label normal'>Patrocinado</div>}
                            {isFeaturedButDoesNotFit() && <div className='product-box-featured-label floating'>Patrocinado</div>}
                            {showSku && product.sku && <div className={`product-box-info-sku${_isFeatured() ? ' big' : ''}`}>SKU: {product.sku}</div>}
                            {product.brand && <div className={`product-box-info-brand${_isFeatured() ? ' big' : ''}`} style={productBoxBrandStyle()}>{product.brand}</div>}
                            <div className={`product-box-info-name${_isFeatured() ? ' big' : ''}`}>{name}</div>
                            {showSeller && (
                                <div className='product-box-info-seller-container'>
                                    <React.Fragment>
                                        {hasSeller() && (
                                            <div className='product-box-info-seller-icon-container'>
                                                <ImageWrapper image={defaultShopIcon} alt='Seller' display='block' width={23} height='auto' loadDelay={0.2}/>
                                            </div>
                                        )}
                                        <div className='product-box-info-seller-message two-lines'>
                                            {hasSeller() && (
                                                <React.Fragment>
                                                    <span>Vendido por </span>
                                                    <span className='product-box-info-seller' style={sellerNameStyle()}>{product.externalSale ? product.sellerName : clientName}</span>
                                                    {mainSellerIcon && !product.externalSale && renderClientIcon()}
                                                </React.Fragment>
                                            )}
                                        </div>
                                    </React.Fragment>
                                </div>
                            )}
                            {showDelivery && (
                                <div className='product-box-info-seller-container'>
                                    <React.Fragment>
                                        {(hasDelivery() || hasInternationalShipping()) && (
                                            <div className='product-box-info-seller-icon-container'>
                                                {hasDelivery() && <ImageWrapper image={defaultTruckIcon} alt='Delivery' display='block' width={23} height='auto' loadDelay={0.2}/>}
                                                {hasInternationalShipping() && <ImageWrapper image={defaultPlaneIcon} alt='Delivery' display='block' width={23} height='auto' loadDelay={0.2}/>}
                                            </div>
                                        )}
                                        <div className='product-box-info-seller-message two-lines'>
                                            {hasDelivery() && (
                                                <React.Fragment>
                                                    <span>Enviado por </span>
                                                    <span className='product-box-info-seller' style={sellerNameStyle()}>{Text.capitalizeFirst(clientName)}</span>
                                                    {mainSellerIcon && !product.externalSale && renderClientIcon()}
                                                </React.Fragment>
                                            )}
                                            {hasInternationalShipping() && <span>Compra internacional</span>}
                                        </div>
                                    </React.Fragment>
                                </div>
                            )}
                            {showRating && (
                                <div className={`product-box-info-rating-container${_isFeatured() ? ' big' : ''}`}>
                                    {!!product.rating && product.rating > 0 && (
                                        <React.Fragment>
                                            <ProductRating id={product.id} rating={product.rating} reviewCount={product.reviewCount}/>
                                        </React.Fragment>
                                    )}
                                </div>
                            )}
                            {product.hasAnyPrice() && (
                                <div className={`product-box-info-prices${_isFeatured() ? ' big' : ''}`}>
                                    {product.exclusivePrice && renderAsExclusivePrice(product.exclusivePrice, product.getExclusiveDiscountPercentage())}
                                    {product.offerPrice && renderAsOfferPrice(product.offerPrice, product.getOfferDiscountPercentage())}
                                    {product.normalPrice && product.normalPrice !== product.offerPrice && (product.offerPrice ? renderAsNormalPrice(product.normalPrice) : renderAsOfferPrice(product.normalPrice, undefined))}
                                </div>
                            )}
                            {showTags && !_isFeatured() && renderTagsList()}
                        </div>
                    </div>
                    {showTags && _isFeatured() && <div className='product-box-info-featured-tags-container'>{renderTagsList()}</div>}
                    <div className='product-box-loader' style={productBoxLoaderWrapperStyle()}>
                        <ProductLoader/>
                    </div>
                </div>
            </div>
        );
    };

    const renderSimpleBox = () => {
        let secondaryPrice;
        let mainPrice;
        let discount;
        if (product.exclusivePrice) {
            mainPrice = product.exclusivePrice;
            secondaryPrice = product.normalPrice;
            discount = product.getExclusiveDiscountPercentage();
        } else {
            if (product.offerPrice) {
                mainPrice = product.offerPrice;
                secondaryPrice = product.normalPrice;
                discount = product.getOfferDiscountPercentage();
            } else {
                mainPrice = product.normalPrice;
                secondaryPrice = undefined;
                discount = undefined;
            }
        }
        return (
            <div
                id={`${idPrefix ? `${idPrefix}-` : ''}${product.id}`}
                data-id={product.id}
                className='product-box'
                style={productBoxStyle()}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                onClick={handleClick}
            >
                <div ref={pulseRef} className='product-box-pulse-cover' style={pulseCoverStyle()}>
                    <div className='product-box-active-cover' style={activeCoverStyle()}>
                        <div className='product-box-top-bar active'>Último visto</div>
                    </div>
                    <div ref={wrapperRef} className='product-box-wrapper simple' style={productBoxWrapperStyle()}>
                        <div className='product-box-media-container simple'>
                            {renderImage()}
                        </div>
                        <div className='product-box-info simple'>
                            {showSku && product.sku && <div className='product-box-info-sku'>SKU: {product.sku}</div>}
                            <div className='product-box-info-name'>{name}</div>
                            {product.hasAnyPrice() && (
                                <div className='product-box-info-prices simple'>
                                    {mainPrice && renderAsOfferPrice(mainPrice, discount)}
                                    {secondaryPrice && renderAsNormalPrice(product.normalPrice)}
                                </div>
                            )}
                            {taxMultiplier && taxMultiplier > 1 && (
                                <div className='product-box-tax-disclaimer' style={taxDisclaimerStyle()}>{taxLabel} Inc.</div>
                            )}
                        </div>
                    </div>
                    <div className='product-box-loader' style={productBoxLoaderWrapperStyle()}>
                        <ProductLoader/>
                    </div>
                </div>
            </div>
        );
    };

    let result;
    switch (design) {
        case 'simple':
            result = renderSimpleBox();
            break;
        default:
            result = renderFullBox();
            break;
    }
    return result;
}

export default ProductBox;